import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Button, ButtonProps, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MantineDropdownMenu, {
  MantineDropdownMenuSection,
  MantineDropdownMenuOption,
} from 'ui-library/menus/MantineDropdownMenu';

export type {MantineDropdownMenuSection as DropdownButtonSection};
export type {MantineDropdownMenuOption as DropdownButtonOption};

export interface DropdownButtonProps
  extends Omit<ButtonProps, 'children'>, Omit<React.ComponentPropsWithoutRef<'button'>, keyof ButtonProps> {
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  sections: Array<MantineDropdownMenuSection>;
}

/**
 * A primary action button with a separate dropdown trigger on the right.
 * Accepts Mantine Button props. When fullWidth is true, the group stretches to 100% and the main
 * button fills remaining space; the caret stays at its natural width.
 * The chevron opens a dropdown menu with the given sections.
 */
export default function DropdownButton({
  children,
  sections,
  disabled,
  onClick,
  leftIcon,
  rightIcon,
  loading,
  loaderPosition,
  loaderProps,
  type,
  variant,
  color,
  size,
  compact,
  uppercase,
  gradient,
  radius,
  fullWidth,
  classNames,
  dataTestAttrs,
}: DropdownButtonProps): JSX.Element {
  const {classes, cx} = useStyles();

  const sharedProps: ButtonProps = {variant, color, size, compact, gradient, radius, classNames};
  const leftButtonProps: Partial<ButtonProps> & Partial<React.ComponentPropsWithoutRef<'button'>> = {
    leftIcon,
    rightIcon,
    disabled,
    onClick,
    uppercase,
    loading,
    loaderPosition,
    loaderProps,
    type,
  };

  const chevron = (
    <Button {...sharedProps} aria-label="More options" className={classes.button} disabled={disabled}>
      <BenchlingIcon icon="chevron-down" />
    </Button>
  );

  return (
    <Button.Group className={cx(classes.root, fullWidth && classes.fullWidth)}>
      <Button
        {...sharedProps}
        {...leftButtonProps}
        className={cx(classes.button, {[classes.flex]: fullWidth})}
        {...(dataTestAttrs ? getTestProps(dataTestAttrs) : {})}
      >
        {children}
      </Button>
      <MantineDropdownMenu sections={sections} trigger={chevron} position="bottom-end" />
    </Button.Group>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    gap: 1,
  },
  fullWidth: {
    width: '100%',
  },
  button: {
    // && bumps specificity above Button's own size-based padding rules
    '&&': {
      padding: `0 ${theme.spacing.sm}`,
    },
  },
  flex: {
    flex: 1,
  },
}));
